/**
 * ProgressDonut.jsx — Iter 68. Ring progress indicator with the percentage in
 * the middle, used on the marketplace banner, the founder engagement tiles and
 * the vendor request queue so one engagement reads the same everywhere.
 *
 * Two stacked circles: a full-circumference track and an arc whose
 * stroke-dasharray is the completed fraction. The rotation puts 0% at twelve
 * o'clock — SVG circles start at three.
 */
import React from 'react';

const clamp = (n) => Math.max(0, Math.min(100, Math.round(Number(n) || 0)));

const ProgressDonut = ({
  pct,
  size = 54,
  stroke = 5,
  color = '#10b981',
  track = 'var(--border)',
  testid,
}) => {
  const value = clamp(pct);
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const filled = (value / 100) * circumference;

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }} data-testid={testid}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: 'rotate(-90deg)', transformOrigin: 'center' }}
        aria-hidden="true"
      >
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={color} strokeWidth={stroke}
          // A round cap on a zero-length dash paints a dot in some renderers,
          // which reads as 1-2% rather than nothing. Square it off at exactly 0.
          strokeLinecap={value === 0 ? 'butt' : 'round'}
          strokeDasharray={`${filled} ${circumference}`}
          style={{ transition: 'stroke-dasharray 500ms ease' }}
        />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center font-mono font-bold antialiased tabular-nums"
        style={{ color, fontSize: Math.round(size * 0.29), letterSpacing: '-0.02em' }}
      >
        {value}%
      </span>
    </div>
  );
};

export default ProgressDonut;
